import { useIntl } from 'react-intl'
import TextLink from 'LuxKit/TextLink'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import CruiseCabinTypeInfoModal from './Modals/CruiseCabinTypeInfoModal'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import { CABIN_CATEGORIES_SORT_ORDER, CruiseType } from 'constants/cruise'
import { setSearchParamValue, deleteSearchParams } from 'lib/url/searchUrlUtils'
import { replace } from 'lib/routing/appRouting'
import { useLocation } from 'react-router-dom'
import { useAppDispatch } from 'hooks/reduxHooks'
import useCruisePriceByNight from 'hooks/useCruisePriceByNight'
import ToggleOptionsGroup from 'LuxKit/ToggleOptionsGroup'
import ToggleOption from 'LuxKit/ToggleOption'
import { useQsQueryParams } from 'hooks/useQueryParams'
import useCruiseFacetFiltersParams from 'hooks/useCruiseFacetFiltersParams'
import styled from 'styled-components'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'

const ToggleOptionContainer = styled.div`
  overflow-x: scroll;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

export type CruiseTypeWithBoth = App.CruiseType | 'BOTH'

/**
 * The cabin type slugs we expect to receive from svc-search.
 */
type CABIN_CATEGORY_SLUG = 'inside' | 'oceanview' | 'window' | 'oceanview_window' | 'balcony' | 'suite'
const CABIN_CATEGORY_SLUGS: Array<CABIN_CATEGORY_SLUG> = ['inside', 'oceanview', 'window', 'oceanview_window', 'balcony', 'suite']

/**
 * The cabin type slugs we display depending on the filtered cruise type.
 */
export type CABIN_CATEGORY_SLUG_RIVER = 'window' | 'balcony' | 'suite'
export type CABIN_CATEGORY_SLUG_BOTH = 'inside' | 'oceanview_window' | 'balcony' | 'suite'
const CABIN_CATEGORIES_SLUGS_RIVER: Array<CABIN_CATEGORY_SLUG_RIVER> = ['window', 'balcony', 'suite']
const CABIN_CATEGORIES_SLUGS_BOTH: Array<CABIN_CATEGORY_SLUG_BOTH> = ['inside', 'oceanview_window', 'balcony', 'suite']

export type CabinTypeMap = {
  [key in CABIN_CATEGORY_SLUG]: {
    minimumPrice: number
    minimumPricePerNight: number
  }
}

interface CruiseSelectCabinTypeProps {
  filters: App.OfferListFilters
  isMobile?: boolean
}

function CruiseSelectCabinType({ filters, isMobile }: CruiseSelectCabinTypeProps) {
  const intl = useIntl()
  const cabinTypeLabels = useCruiseCabinTypeLabels()
  const dispatch = useAppDispatch()
  const isPricePerNight = useCruisePriceByNight()
  const showModal = useContext(ModalContext)
  const { search } = useLocation()
  const { facetParams } = useCruiseFacetFiltersParams({ filters })
  const { cabinTypes: cabinTypesParam } = useQsQueryParams<{ cabinTypes?: string | Array<string> }>()
  const initialCabinSlug = useMemo(() => {
    if (!cabinTypesParam) return undefined
    const types = Array.isArray(cabinTypesParam) ? cabinTypesParam : [cabinTypesParam]
    if (types.length > 1 && types.includes('oceanview') && types.includes('window')) {
      return 'oceanview_window'
    }
    return types[0]
  }, [cabinTypesParam])
  const [selectedCabinType, setSelectedCabinType] = useState<string | undefined>(initialCabinSlug)
  const priceProperties = isPricePerNight ? 'minimumPricePerNight' : 'minimumPrice'
  const [facetsV2] = useCruiseSearchFacets(facetParams)

  const cruiseType = useMemo(() => {
    const activeCruiseTypes = facetsV2.cruiseTypes.filter(facet => facet.count)

    if (activeCruiseTypes.length === 1) {
      return activeCruiseTypes[0].slug.toUpperCase() as CruiseTypeWithBoth
    }

    return 'BOTH'
  }, [facetsV2.cruiseTypes])

  const cabinCategories = useMemo(() => {
    if (cruiseType === CruiseType.RIVER) {
      return CABIN_CATEGORIES_SLUGS_RIVER
    } else if (cruiseType === 'BOTH') {
      return CABIN_CATEGORIES_SLUGS_BOTH
    }

    return CABIN_CATEGORIES_SORT_ORDER
  }, [cruiseType])

  const mappedCabinTypes = useMemo(() => {
    let minPriceOfOceanviewAndWindow: number | undefined = undefined
    let minPricePerNightOfOceanviewAndWindow: number | undefined = undefined

    return facetsV2.cabinTypes.reduce((cabinTypes, facet) => {
      if (CABIN_CATEGORY_SLUGS.includes(facet.slug as CABIN_CATEGORY_SLUG)) {
        if (cruiseType === 'BOTH' && (facet.slug === 'oceanview' || facet.slug === 'window')) {
          if (!minPriceOfOceanviewAndWindow || (facet.min && minPriceOfOceanviewAndWindow > facet.min)) {
            minPriceOfOceanviewAndWindow = facet.min
            minPricePerNightOfOceanviewAndWindow = facet.minPerNight

            cabinTypes['oceanview_window' as CABIN_CATEGORY_SLUG] = {
              minimumPrice: minPriceOfOceanviewAndWindow ?? 0,
              minimumPricePerNight: minPricePerNightOfOceanviewAndWindow ?? 0,
            }
          }
        } else {
          cabinTypes[facet.slug as CABIN_CATEGORY_SLUG] = {
            minimumPrice: facet.min ?? 0,
            minimumPricePerNight: facet.minPerNight ?? 0,
          }
        }
      }

      return cabinTypes
    }, {} as CabinTypeMap)
  }, [facetsV2.cabinTypes, cruiseType])

  const handleSelectedCabinType = useCallback((nextCabinType: string) => {
    if (nextCabinType !== selectedCabinType) {
      const nextSearch = setSearchParamValue(search, 'cabinTypes', nextCabinType === 'oceanview_window' ? ['oceanview', 'window'] : nextCabinType)

      dispatch(replace({ search: nextSearch }))
      setSelectedCabinType(nextCabinType)
    } else {
      const nextSearch = deleteSearchParams(search, 'cabinTypes')

      dispatch(replace({ search: nextSearch }))
      setSelectedCabinType(undefined)
    }
  }, [dispatch, search, selectedCabinType])

  const openModal = useCallback(async() => {
    const selectedCabinType = await showModal<string | undefined>(<CruiseCabinTypeInfoModal cabinTypeMap={mappedCabinTypes} cabinTypeLabels={cabinCategories} />)
    if (selectedCabinType) {
      handleSelectedCabinType(selectedCabinType)
    }
  }, [showModal, mappedCabinTypes, handleSelectedCabinType, cabinCategories])

  // Sync local state with URL params when cabinType changes
  useEffect(() => {
    setSelectedCabinType(initialCabinSlug)
  }, [initialCabinSlug])

  const fromCaption = intl.formatMessage({
    id: 'nl1u23SZIfgMVU5nG_tF',
    defaultMessage: 'From',
    description: 'Price caption indicating starting price for cabin type in search filter',
  })

  const perNightUnit = intl.formatMessage({
    id: '-d4n8q12-EYhqsjizAVR',
    defaultMessage: '/night',
    description: 'Price unit suffix for per-night cabin type pricing in search filter',
  })

  const perPersonUnit = intl.formatMessage({
    id: '3yktjcFffYmPeKwCc1Ko',
    defaultMessage: '/person',
    description: 'Price unit suffix for per-person cabin type pricing in search filter',
  })

  return <Group direction="vertical" gap={16}>
    <Group direction="horizontal" gap={16} horizontalAlign="space-between">
      <Heading variant="heading6">
        {intl.formatMessage({
          id: '1mm2b-pvFRMBwNUHaZ36',
          defaultMessage: 'Select a cabin type',
          description: 'Heading for the cabin type selection section in cruise search',
        })}
      </Heading>
      <TextLink size="small" weight="regular" onClick={openModal}>
        {intl.formatMessage({
          id: '14JpkyD4vp3Xk6I3l2gM',
          defaultMessage: 'Which cabin type is right for me?',
          description: 'Link text to open a modal explaining the different cruise cabin types',
        })}
      </TextLink>
    </Group>
    <ToggleOptionContainer>
      <ToggleOptionsGroup horizontalAlign="start" desktopHorizontalAlign="stretch">
        {cabinCategories.map((cabinType) => (
          <ToggleOption
            key={cabinType}
            isSelected={selectedCabinType === cabinType}
            onClick={() => handleSelectedCabinType(cabinType)}
            subtitle={!isMobile && <Group direction="horizontal" verticalAlign="center" gap={2}>
              <PriceRowPriceWithCaption
                price={mappedCabinTypes[cabinType]?.[priceProperties]}
                size="S"
                caption={fromCaption}
                saleUnit={isPricePerNight ? perNightUnit : perPersonUnit}
              />
            </Group>}
            title={cabinTypeLabels[cabinType] ?? cabinType}
          />
        ))}
      </ToggleOptionsGroup>
    </ToggleOptionContainer>
  </Group>
}

export default CruiseSelectCabinType
